/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { getPostureStatus } from '@augmentt-dev/ui-components/src/reports/SecurityPosture/utils'
import { AUGMENTT_POSTURE_STATES } from '@augmentt-dev/ui-components/src/reports/SecurityPosture/constants'
import { useGetCustomerSecurityPosture, useGetCustomerSecurityPostureChecks } from './secure'
import prepareHeaders from '../utils/prepareHeaders'
import { getSelectedCustomerId } from '../selectors/customers'

const reducerPath = 'assessments'
const base = '/secure/assessment'
const url = `${base}/*`

export const assessmentsApi = createApi({
  reducerPath,
  tagTypes: [reducerPath],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getTemplates: builder.query({
      providesTags(_, __, id) {
        return [{ type: reducerPath, id }]
      },
      query: (customerId = '*') => `${base}/${customerId}`,
    }),
    createTemplate: builder.mutation({
      invalidatesTags: [reducerPath],
      query: (body) => ({
        body,
        url,
        method: 'POST',
      }),
    }),
    updateTemplate: builder.mutation({
      invalidatesTags: [reducerPath],
      query: ({ id, ...body }) => ({
        body,
        url: `${url}/${id}`,
        method: 'PUT',
      }),
    }),
    updateAssignments: builder.mutation({
      invalidatesTags: [reducerPath],
      query: ({ id, ...body }) => ({
        body,
        url: `${url}/${id}/assignments`,
        method: 'PUT',
      }),
    }),
    deleteTemplate: builder.mutation({
      invalidatesTags: [reducerPath],
      query: (id) => ({
        url: `${url}/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useGetTemplatesQuery,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useUpdateAssignmentsMutation,
  useUpdateTemplateMutation,
} = assessmentsApi

export const useGetAllTemplates = () => useGetTemplatesQuery('*')

export const useGetCustomerTemplates = () => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetTemplatesQuery(customerId)
}

export const useGetCustomerTemplate = (templateId) => {
  const { data = [], ...rest } = useGetCustomerTemplates()

  return {
    data: data.find(({ id }) => id === parseInt(templateId, 10)),
    ...rest,
  }
}

export const useGetCustomerTemplateCompliance = () => {
  const { data: templates = [] } = useGetCustomerTemplates()
  const { data: securityPostureChecks, isLoading } = useGetCustomerSecurityPostureChecks()

  return useMemo(
    () =>
      securityPostureChecks
        ? {
            data: templates.reduce(
              (result, { id, checks }) =>
                checks.length
                  ? {
                      ...result,
                      [id]: checks.reduce(
                        (complianceResult, check) => {
                          const posture = securityPostureChecks.find((item) => item.checkId === check)

                          if (posture) {
                            switch (getPostureStatus(posture)) {
                              case AUGMENTT_POSTURE_STATES.COMPLIANT:
                                complianceResult.compliant++
                                break
                              case AUGMENTT_POSTURE_STATES.NOT_COMPLIANT:
                                complianceResult.nonCompliant++
                                break
                              case AUGMENTT_POSTURE_STATES.PARTIALLY_COMPLIANT:
                                complianceResult.partial++
                                break
                              default:
                                complianceResult.other++
                                break
                            }

                            complianceResult.total++
                            complianceResult.percentage =
                              (complianceResult.compliant + complianceResult.partial * 0.5) / complianceResult.total
                          }

                          return complianceResult
                        },
                        {
                          total: 0,
                          compliant: 0,
                          partial: 0,
                          nonCompliant: 0,
                          percentage: 0,
                          other: 0,
                        },
                      ),
                    }
                  : result,
              {},
            ),
          }
        : { isLoading },
    [templates, securityPostureChecks, isLoading],
  )
}

export const useGetCustomerTemplateChecks = (templateId) => {
  const { data: securityPosture, isLoading: isSecurityPostureLoading } = useGetCustomerSecurityPosture()
  const { data: templates = [], isLoading: isTemplatesLoading } = useGetCustomerTemplates()
  const isLoading = isSecurityPostureLoading || isTemplatesLoading

  return useMemo(() => {
    if (!securityPosture || !templateId || isLoading) {
      return { isLoading }
    }

    const { securityChecks: { allMonitored = [], ignored = [] } = {} } = securityPosture
    const { checks = [] } = templates.find(({ id }) => id === templateId) ?? {}

    return {
      data: [...allMonitored, ...ignored].filter(({ checkId }) => checks.includes(checkId)),
    }
  }, [securityPosture, templateId, templates, isLoading])
}
